import { useMemo, useState } from "react";
import { portion, LEBENSMITTEL, LEBENSMITTEL_KEYS } from "@/lib/portionen";
import { trackSettled, track } from "@/lib/track";

const de = (v) => (Number(v) || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export default function PortionsRechner({ food: initialFood = "reis" }) {
  const [food, setFood] = useState(LEBENSMITTEL_KEYS.includes(initialFood) ? initialFood : "reis");
  const [personen, setPersonen] = useState(4);
  const [modus, setModus] = useState("hauptgericht");

  const res = useMemo(() => portion(food, personen, modus), [food, personen, modus]);

  const switchFood = (f) => { setFood(f); track("portion_lebensmittel", { calculator: f }); };
  const upd = () => trackSettled("portion_berechnet", { calculator: food });

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-6 shadow-sm">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Lebensmittel</span>
            <select
              value={food}
              onChange={(e) => switchFood(e.target.value)}
              data-testid="food-select"
              className="rounded-lg border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40"
            >
              {LEBENSMITTEL_KEYS.map((k) => (
                <option key={k} value={k}>{LEBENSMITTEL[k].label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Anzahl Personen</span>
            <div className="flex items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-primary/40">
              <input type="number" inputMode="numeric" min={1} value={personen}
                onChange={(e) => { setPersonen(e.target.value === "" ? "" : Number(e.target.value)); upd(); }}
                className="w-full bg-transparent px-3 py-2 outline-none" />
              <span className="px-3 text-sm text-muted-foreground">Pers.</span>
            </div>
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Als…</span>
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/40 w-fit">
              {[["hauptgericht", "Hauptgericht"], ["beilage", "Beilage"]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => { setModus(val); upd(); }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${modus === val ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-secondary/60 p-4">
            <div className="text-sm text-muted-foreground">{LEBENSMITTEL[food].label} für {res.personen} {res.personen === 1 ? "Person" : "Personen"}</div>
            <div className="text-2xl font-semibold mt-0.5" data-testid="portion-ergebnis">
              {res.gesamtGramm >= 1000 ? `${de(res.gesamtKg)} kg` : `${de(res.gesamtGramm)} g`}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {de(res.proPerson)} g pro Person ({modus === "beilage" ? "Beilage" : "Hauptgericht"})
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Mengen roh/ungekocht. Reis und Nudeln nehmen beim Kochen etwa das 2,5- bis 3-fache an Gewicht zu. Werte sind Richtwerte und hängen von Appetit und Beilagen ab.
          </p>
        </div>
      </div>
    </div>
  );
}
